import React, { useState } from "react";
import { Form, Input, Button, Tabs, message, Divider, Card } from "antd";
import {
	UserOutlined,
	LockOutlined,
	BankOutlined,
	ArrowRightOutlined,
} from "@ant-design/icons";
import GoogleLoginButton from "./Gmailverification";
import "./LoginPage.css";
import Hexapi from "../../utility/HexAPI/Hexapi";
import { setAuth } from "../../Redux/Store";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import trackleadslogo from "../../assets/trackleadslogo.png";
import { showConfirm } from "../../utility/showconfirm";

/* ------------------------------------------------------------------ */
/*  Password policy (shared by Register + Change Password)             */
/*  NOTE: quotes (' ") and spaces are blocked because queries are      */
/*  built as '{0}' strings. Remove that rule only if Hexapi escapes    */
/*  values / uses parameters.                                          */
/* ------------------------------------------------------------------ */
const passwordRules = [
	{ required: true, message: "Password is required" },
	{ min: 8, message: "Minimum 8 characters" },
	{ pattern: /[A-Z]/, message: "At least 1 uppercase letter" },
	{ pattern: /[a-z]/, message: "At least 1 lowercase letter" },
	{ pattern: /\d/, message: "At least 1 number" },
	{
		pattern: /[!@#$%^&*()_+\-=[\]{};:,.?]/,
		message: "At least 1 special character (!@#$%^&*()_+-=[]{};:,.?)",
	},
	{ pattern: /^[^'"\s]+$/, message: "Quotes and spaces are not allowed" },
];

const confirmRule =
	(name = "password") =>
	({ getFieldValue }) => ({
		validator(_, value) {
			return !value || getFieldValue(name) === value
				? Promise.resolve()
				: Promise.reject(new Error("Passwords do not match"));
		},
	});

export default function LoginPage({ onAuthSuccess }) {
	const [mode, setMode] = useState("login");
	// loginStep: 0 = email, 1 = password, 2 = change expired password
	const [loginStep, setLoginStep] = useState(0);
	const [loginUsername, setLoginUsername] = useState("");
	const [loginInfo, setLoginInfo] = useState(null);
	const [expiredCtx, setExpiredCtx] = useState(null);
	const [registerStep, setRegisterStep] = useState(0);
	const [registerAccountStep, setRegisterAccountStep] = useState(0);
	const [registerEmail, setRegisterEmail] = useState("");
	const [loginForm] = Form.useForm();
	const [loginPasswordForm] = Form.useForm();
	const [changePwdForm] = Form.useForm();
	const [step1Form] = Form.useForm();
	const [step2EmailForm] = Form.useForm();
	const [step2PasswordForm] = Form.useForm();
	const [orgData, setOrgData] = useState({ OrgnisationName: "" });
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const switchMode = (key, prefillEmail) => {
		setMode(key);
		setRegisterStep(0);
		setRegisterAccountStep(0);
		setRegisterEmail("");
		setLoginStep(0);
		setLoginUsername("");
		setLoginInfo(null);
		setExpiredCtx(null);
		loginForm.resetFields();
		loginPasswordForm.resetFields();
		changePwdForm.resetFields();
		step2EmailForm.resetFields();
		step2PasswordForm.resetFields();

		if (key === "login" && prefillEmail) {
			loginForm.setFieldsValue({ username: prefillEmail });
		}
	};

	const checkEmailExists = async (username) => {
		const obj = {
			query: `[dbo].[CRM_Proc_Check_Email_Exists] @username='{0}'`,
			queryArr: [`${username}`],
			dbname: "CRM_Dbase_Master",
			serverip: "trackhr.co.in",
			username: "AlishaHex",
			password: "123@@HexDev",
		};
		const data = await Hexapi(obj);
		const result = data?.[""]?.[0];
		return {
			exists: result?.command == 1,
			msg: result?.msg,
			userData: result?.command == 1 ? data?.["logininfo"]?.[0] : null,
		};
	};

	const generateSessionID = () => {
		if (typeof crypto !== "undefined" && crypto.randomUUID) {
			return crypto.randomUUID();
		}
		return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
			const r = (Math.random() * 16) | 0;
			const v = c === "x" ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	};

	/*
	 * CRM_Proc_Login_CheckV2 should return:
	 *   command = 1 -> success
	 *   command = 2 -> password expired (30 days) -> no session created
	 *   else        -> failure
	 */
	const runLoginCheck = async ({ logininfo, username, password }) => {
		const sessionId = generateSessionID();
		localStorage.setItem("sessionid", sessionId);
		const query = password
			? `[dbo].[CRM_Proc_Login_CheckV2] @SessionID='{0}', @Username='{1}', @Password='{2}'`
			: `[dbo].[CRM_Proc_Login_CheckV2] @SessionID='{0}', @Username='{1}'`;

		const queryArr = password
			? [sessionId, username, password]
			: [sessionId, username];

		const obj = {
			query,
			queryArr,
			dbname: logininfo?.dbname,
			serverip: logininfo?.serverip,
			username: logininfo?.dbusername,
			password: logininfo?.dbpassword,
		};

		const data = await Hexapi(obj);
		const result = data?.[""]?.[0];

		return {
			success: result?.command == 1,
			expired: result?.command == 2,
			msg: result?.Msg || result?.msg,
			sessionId,
			result: result,
		};
	};

	const handleLoginUsernameSubmit = async (values) => {
		if (!values.username) return;
		setLoading(true);
		try {
			const { exists, userData } = await checkEmailExists(values.username);

			if (!exists) {
				message.error("This account is not registered. Please register first.");
				switchMode("register");
				return;
			}

			setLoginUsername(values.username);
			setLoginInfo(userData ?? null);
			setLoginStep(1);
		} catch (err) {
			console.error(err);
			message.error("error");
		} finally {
			setLoading(false);
		}
	};

	const handleLoginPasswordSubmit = async (
		values,
		autousername,
		autopassword,
		logindata,
	) => {
		const valuepasword = autopassword ? autopassword : values?.password;
		if (!valuepasword) return;
		setLoading(true);
		try {
			const activeLoginInfo = logindata ? logindata : loginInfo;
			const activeUsername = autousername ? autousername : loginUsername;

			const { success, expired, msg, sessionId, result } = await runLoginCheck({
				logininfo: activeLoginInfo,
				username: activeUsername,
				password: valuepasword,
			});

			
			// if (expired) {
				// localStorage.removeItem("sessionid");
				// setExpiredCtx({
				// 	logininfo: activeLoginInfo,
				// 	username: activeUsername,
				// 	oldPassword: valuepasword,
				// });
				// setLoginStep(2);
				// message.warning("Your password has expired. Please set a new password.");
				// return;
			// }

			if (!success) {
				localStorage.removeItem("sessionid");
				message.error(msg || "Invalid password.");
				return;
			}

			dispatch(
				setAuth({
					loginName: activeUsername,
					orgdbName: activeLoginInfo?.dbname,
					serverip: activeLoginInfo?.serverip,
					dbusername: activeLoginInfo?.dbusername,
					dbpassword: activeLoginInfo?.dbpassword,
					userId: result.userid,
					dbid: activeLoginInfo?.dbid,
					orgname: activeLoginInfo?.orgname,
				}),
			);

			setTimeout(() => {
				navigate("/", { replace: true });
			}, 500);
		} catch (err) {
			console.error(err);
			message.error("error");
		} finally {
			setLoading(false);
		}
	};

	const handleChangePassword = async (values) => {
		if (!expiredCtx) return;
		setLoading(true);
		try {
			const { logininfo, username, oldPassword } = expiredCtx;
			const data = await Hexapi({
				query: `[dbo].[CRM_Proc_Change_Password] @Username='{0}', @OldPassword='{1}', @NewPassword='{2}'`,
				queryArr: [username, oldPassword, values.newPassword],
				dbname: logininfo?.dbname,
				serverip: logininfo?.serverip,
				username: logininfo?.dbusername,
				password: logininfo?.dbpassword,
			});

			const res = data?.[""]?.[0];
			if (res?.command != 1) {
				message.error(res?.msg || "Failed to change password.");
				return;
			}

			message.success("Password changed successfully");
			const newPassword = values.newPassword;
			setExpiredCtx(null);
			changePwdForm.resetFields();

			await handleLoginPasswordSubmit("", username, newPassword, logininfo);
		} catch (err) {
			console.error(err);
			message.error("error");
		} finally {
			setLoading(false);
		}
	};

	const handleLoginBack = () => {
		setLoginStep(0);
		setLoginUsername("");
		setLoginInfo(null);
		setExpiredCtx(null);
		loginPasswordForm.resetFields();
		changePwdForm.resetFields();
	};

	const handleStep1Next = (values) => {
		setOrgData(values);
		setRegisterStep(1);
		setRegisterAccountStep(0);
	};

	const handleStep2Back = () => {
		setRegisterStep(0);
		setRegisterAccountStep(0);
	};

	const handleRegisterEmailSubmit = async (values) => {
		if (!values.username) return;
		setLoading(true);
		try {
			const { exists } = await checkEmailExists(values.username);
			if (exists) {
				message.error(
					"This email is already registered. Please login instead.",
				);
				switchMode("login", values.username);
				return;
			}
			setRegisterEmail(values.username);
			setRegisterAccountStep(1);
		} catch (err) {
			console.error(err);
			message.error("error");
		} finally {
			setLoading(false);
		}
	};

	const handleRegisterAccountBack = () => {
		setRegisterAccountStep(0);
		setRegisterEmail("");
		step2PasswordForm.resetFields();
	};

	const handleRegisterPasswordSubmit = async (values) => {
		setLoading(true);
		try {
			const obj = {
				query: `[dbo].[CRM_Proc_Org_Register] @username ='{0}', @OrgnisationName='{1}', @EmailID='{2}'`,
				queryArr: [`${registerEmail}`, `${orgData.OrgnisationName}`, 0],
				dbname: "CRM_Dbase_Master",
				serverip: "trackhr.co.in",
				username: "AlishaHex",
				password: "123@@HexDev",
			};
			const data = await Hexapi(obj);

			if (data?.[""]?.[0]?.command == 0) {
				if (data?.[""]?.[0]?.msg == "EMAIL_EXISTS") {
					message.error(
						"This email is already registered. Please login instead.",
					);
					switchMode("login", registerEmail);
				} else {
					message.error(data?.[""]?.[0]?.msg || "Registration failed.");
				}
				return;
			}

			if (data?.["logininfo"]?.[0]?.command == 1) {
				const logininfo = data?.["logininfo"]?.[0];
				setLoginInfo(logininfo);
				const obj = {
					query: `[CRM_Proc_User_Register_Submit]  @username='{0}',@password='{1}',@orgname='{2}'`,
					queryArr: [
						`${registerEmail}`,
						`${values.password}`,
						`${orgData.OrgnisationName}`,
					],
					dbname: logininfo?.dbname,
					serverip: logininfo?.serverip,
					username: logininfo?.dbusername,
					password: logininfo?.dbpassword,
				};
				const resp = await Hexapi(obj);
				if (resp?.[""][0].command == 1) {
					handleLoginPasswordSubmit(
						"",
						resp?.[""][0].username,
						resp?.[""][0].password,
						logininfo,
					);
				}
			}
		} catch (err) {
			console.log(err);
			message.error("error");
		} finally {
			setLoading(false);
		}
	};

	const handleGoogleLogin = async (userInfo, username, logindata) => {
		if (mode === "register" && (!orgData.OrgnisationName || !orgData.phone)) {
			message.error("Please complete Organisation Name");
			setRegisterStep(0);
			return;
		}
		if (userInfo.email_verified == true) {
			setLoading(true);
			try {
				const { exists, userData } = await checkEmailExists(userInfo.email);

				if (!exists) {
					message.error(
						"This account is not registered. Please register first.",
					);
					switchMode("register");
					return;
				}

				// Google login has no password -> expiry does not apply
				const { success, msg, result } = await runLoginCheck({
					logininfo: userData,
					username: userInfo.email,
				});

				if (!success) {
					message.error(msg || "Login failed.");
					return;
				}
				dispatch(
					setAuth({
						loginName: userInfo.email,
						orgdbName: userData?.dbname,
						serverip: userData?.serverip,
						dbusername: userData?.dbusername,
						dbpassword: userData?.dbpassword,
						userId: result.userid,
						dbid: userData?.dbid,
						orgname: userData?.orgname,
					}),
				);

				setTimeout(() => {
					navigate("/", { replace: true });
				}, 500);
			} catch (err) {
				console.error(err);
				message.error("error");
			} finally {
				setLoading(false);
			}
		}
	};

	const handleGoogleRegister = async (userInfo) => {
		if (mode === "register" && !orgData.OrgnisationName) {
			message.error("Please complete Organisation Name");
			setRegisterStep(0);
			return;
		}
		if (userInfo.email_verified == true) {
			setLoading(true);
			try {
				const { exists } = await checkEmailExists(userInfo.email);
				if (exists) {
					message.error(
						"This email is already registered. Please login instead.",
					);
					switchMode("login", userInfo.email);
					return;
				}
				const obj = {
					query: `[dbo].[CRM_Proc_Org_Register] @username ='{0}', @OrgnisationName='{1}', @EmailID='{2}'`,
					queryArr: [`${userInfo.email}`, `${orgData.OrgnisationName}`, 0],
					dbname: "CRM_Dbase_Master",
					serverip: "trackhr.co.in",
					username: "AlishaHex",
					password: "123@@HexDev",
				};
				const data = await Hexapi(obj);

				if (data?.[""]?.[0]?.command == 0) {
					if (data?.[""]?.[0]?.msg == "EMAIL_EXISTS") {
						message.error(
							"This email is already registered. Please login instead.",
						);
						switchMode("login", userInfo.email);
					} else {
						message.error(data?.[""]?.[0]?.msg || "Registration failed.");
					}
					return;
				}

				if (data?.["logininfo"]?.[0]?.command == 1) {
					const logininfo = data?.["logininfo"]?.[0];
					const obj = {
						query: `[CRM_Proc_User_Register_Submit] @username='{0}',@password='{1}',@orgname='{2}'`,
						queryArr: [`${userInfo.email}`, "", `${orgData.OrgnisationName}`],
						dbname: logininfo?.dbname,
						serverip: logininfo?.serverip,
						username: logininfo?.dbusername,
						password: logininfo?.dbpassword,
					};
					const resp = await Hexapi(obj);

					showConfirm({
						content: resp[""][0]?.msg,
						hideCancel: true,
					});
				}
			} catch (err) {
				console.log(err);
				message.error("error");
			} finally {
				setLoading(false);
			}
		}
	};

	const renderLoginBody = () => {
		// Step 0: email
		if (loginStep === 0) {
			return (
				<Form
					form={loginForm}
					layout="vertical"
					onFinish={handleLoginUsernameSubmit}
					className="login-form"
				>
					<Form.Item
						name="username"
						rules={[{ required: true, message: "Email is required" }]}
					>
						<Input
							size="large"
							prefix={<UserOutlined />}
							placeholder="Email"
							autoComplete="username"
						/>
					</Form.Item>
					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={loading}
							block
							size="large"
							className="login-btn-primary"
						>
							Continue <ArrowRightOutlined />
						</Button>
					</Form.Item>
				</Form>
			);
		}

		// Step 2: password expired -> change password
		if (loginStep === 2) {
			return (
				<Form
					form={changePwdForm}
					layout="vertical"
					onFinish={handleChangePassword}
					className="login-form"
				>
					<div className="login-identity-chip">
						<UserOutlined /> {expiredCtx?.username}
					</div>
					<Form.Item
						name="newPassword"
						rules={[
							...passwordRules,
							({ getFieldValue }) => ({
								validator(_, value) {
									return !value || value !== expiredCtx?.oldPassword
										? Promise.resolve()
										: Promise.reject(
												new Error("New password must be different from old"),
											);
								},
							}),
						]}
					>
						<Input.Password
							size="large"
							prefix={<LockOutlined />}
							placeholder="New password"
							autoComplete="new-password"
							autoFocus
						/>
					</Form.Item>
					<Form.Item
						name="confirmNewPassword"
						dependencies={["newPassword"]}
						rules={[
							{ required: true, message: "Confirm Password is required" },
							confirmRule("newPassword"),
						]}
					>
						<Input.Password
							size="large"
							prefix={<LockOutlined />}
							placeholder="Confirm new password"
							autoComplete="new-password"
						/>
					</Form.Item>
					<Form.Item>
						<div className="login-step-actions">
							<Button onClick={handleLoginBack} block size="large">
								Cancel
							</Button>
							<Button
								type="primary"
								htmlType="submit"
								loading={loading}
								block
								size="large"
								className="login-btn-primary"
							>
								Update password
							</Button>
						</div>
					</Form.Item>
				</Form>
			);
		}

		// Step 1: password
		return (
			<Form
				form={loginPasswordForm}
				layout="vertical"
				onFinish={handleLoginPasswordSubmit}
				className="login-form"
			>
				<div className="login-identity-chip">
					<UserOutlined /> {loginUsername}
				</div>
				<Form.Item
					name="password"
					rules={[{ required: true, message: "Password is required" }]}
				>
					<Input.Password
						size="large"
						prefix={<LockOutlined />}
						placeholder="Password"
						autoComplete="current-password"
						autoFocus
					/>
				</Form.Item>
				<Form.Item>
					<div className="login-step-actions">
						<Button onClick={handleLoginBack} block size="large">
							Back
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							loading={loading}
							block
							size="large"
							className="login-btn-primary"
						>
							Login
						</Button>
					</div>
				</Form.Item>
			</Form>
		);
	};

	return (
		<div className="login-page">
			<div className="login-visual">
				<div className="login-visual-grid" />
				<div className="login-visual-content">
					<div className="login-logo">
						<img
							src={trackleadslogo}
							className="img-fluid"
							style={{ width: "200px" }}
						/>
					</div>

					<h1 className="login-headline">
						Every lead, call and deal —{" "}
						<span className="login-headline-accent">tracked to close.</span>
					</h1>
					<div>
						<p className="login-widgets-label">Your pipeline at a glance</p>
						<div className="login-widgets">
							<div className="login-widget-card">
								<div className="login-widget-head">
									<div className="login-widget-head-left">
										<span
											className="material-symbols-outlined login-widget-icon"
											aria-hidden="true"
										>
											▲
										</span>
										<span>Revenue Velocity</span>
									</div>
									<span className="login-widget-delta">+12.4%</span>
								</div>
								<p className="login-widget-sub">Last 6 Months</p>
								<div className="login-chart-bars">
									<div className="login-chart-bar" style={{ height: "35%" }} />
									<div className="login-chart-bar" style={{ height: "55%" }} />
									<div className="login-chart-bar" style={{ height: "45%" }} />
									<div
										className="login-chart-bar"
										style={{ height: "70%", background: "rgba(255,193,7,0.4)" }}
									/>
									<div
										className="login-chart-bar"
										style={{ height: "65%", background: "rgba(255,193,7,0.6)" }}
									/>
									<div
										className="login-chart-bar is-strong"
										style={{ height: "90%" }}
									/>
								</div>
								<div className="login-chart-labels">
									<span>Jan</span>
									<span>Feb</span>
									<span>Mar</span>
									<span>Apr</span>
									<span>May</span>
									<span>Jun</span>
								</div>
							</div>
							<div className="login-widget-card">
								<div className="login-widget-head">
									<span
										className="login-widget-sub"
										style={{ marginBottom: 0 }}
									>
										Active Pipeline
									</span>
									<span className="login-pipeline-live">
										<span className="login-pipeline-dot" />
										Live
									</span>
								</div>
								<div className="login-pipeline-stats">
									<div>
										<p className="login-pipeline-stat-label">Active Deals</p>
										<p className="login-pipeline-stat-value">42</p>
									</div>
									<div>
										<p className="login-pipeline-stat-label">Pipeline Value</p>
										<p className="login-pipeline-stat-value">₹1.2Cr</p>
									</div>
								</div>
								<div className="login-pipeline-bar-track">
									<div className="login-pipeline-bar-fill" />
								</div>
								<div className="login-pipeline-bar-labels">
									<span>New Leads</span>
									<span>Closed Deals</span>
								</div>
							</div>
						</div>
					</div>
					<div className="login-partner-footer">
						<span className="login-partner-label">
							Developed and marketed by
						</span>
						<div className="login-partner-row">
							<img
								className="login-partner-logo"
								alt="Hex Business Innovations Logo"
								src="https://lh3.googleusercontent.com/aida-public/AB6AXuBcRmQx22ExVw72oOggoOSHBVZ1jBjXE-We0JRguuhBOx1ZvSl2u2ceINprJXiVFZ4utyI5h8HTSIHAI7qcE_8huMd7RJG3gKvDox0ttjb1JlbxktfD37anhlPpwxCDklew2-_m2OuuAlEQhmAbbE-x39_ZW5avwxkEQfY1k0JrG_CGvwHxBaw6MQayvhBb8aQgXLdhxdfOh_z5txkV90pnUXQ5QgBssY6-0dNEJMjOi3nOAf-VX2lUUNs4Ych8EwtCQQ"
							/>
							<span className="login-partner-name">
								Hex Business Innovations | Technology &amp; Software Partners
							</span>
						</div>
					</div>
					<div className="login-badge">
						<div className="login-badge-strip" />
						<div className="login-badge-row">
							<div className="login-badge-avatar" />
							<div className="login-badge-lines">
								<div className="login-badge-line w-70" />
								<div className="login-badge-line w-40" />
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="login-form-panel">
				<Card className="login-card" bordered={false}>
					<div className="login-card-head">
						<h2 className="login-title">
							{mode === "login"
								? loginStep === 2
									? "Password expired"
									: "Welcome back"
								: "Create your workspace"}
						</h2>
						<p className="login-subtitle">
							{mode === "login"
								? loginStep === 2
									? "Passwords must be changed every 30 days. Set a new one to continue."
									: "Sign in to your WorkSpace"
								: "Set up your organisation in two quick steps"}
						</p>
					</div>

					<Tabs
						activeKey={mode}
						centered
						onChange={(key) => switchMode(key)}
						className="login-tabs"
						items={[
							{ key: "login", label: "Login" },
							{ key: "register", label: "Register" },
						]}
					/>

					{mode === "login" ? (
						renderLoginBody()
					) : (
						<>
							<div className="login-progress">
								<div
									className={`login-progress-step ${registerStep >= 0 ? "is-active" : ""}`}
								>
									<span className="login-progress-label">Organisation</span>
								</div>
								<div className="login-progress-line">
									<div
										className="login-progress-fill"
										style={{ width: registerStep >= 1 ? "100%" : "0%" }}
									/>
								</div>
								<div
									className={`login-progress-step ${registerStep >= 1 ? "is-active" : ""}`}
								>
									<span className="login-progress-label">Account</span>
								</div>
							</div>

							{registerStep === 0 ? (
								<Form
									form={step1Form}
									layout="vertical"
									initialValues={orgData}
									onFinish={handleStep1Next}
									className="login-form"
								>
									<Form.Item
										name="OrgnisationName"
										rules={[
											{
												required: true,
												message: "Organisation Name is required",
											},
										]}
									>
										<Input
											size="large"
											prefix={<BankOutlined />}
											placeholder="Company name"
											autoComplete="organization"
										/>
									</Form.Item>
									<Form.Item>
										<Button
											type="primary"
											htmlType="submit"
											block
											size="large"
											className="login-btn-primary"
										>
											Continue <ArrowRightOutlined />
										</Button>
									</Form.Item>
								</Form>
							) : registerAccountStep === 0 ? (
								<Form
									form={step2EmailForm}
									layout="vertical"
									onFinish={handleRegisterEmailSubmit}
									className="login-form"
								>
									<Form.Item
										name="username"
										rules={[
											{ required: true, message: "Email is required" },
											{ type: "email", message: "Enter a valid email" },
										]}
									>
										<Input
											size="large"
											prefix={<UserOutlined />}
											placeholder="Work email"
											autoComplete="email"
										/>
									</Form.Item>
									<Form.Item>
										<div className="login-step-actions">
											<Button onClick={handleStep2Back} block size="large">
												Back
											</Button>
											<Button
												type="primary"
												htmlType="submit"
												loading={loading}
												block
												size="large"
												className="login-btn-primary"
											>
												Continue
											</Button>
										</div>
									</Form.Item>

									<Divider plain className="login-divider">
										OR
									</Divider>
									<div className="login-google-wrap">
										<GoogleLoginButton onLogin={handleGoogleRegister} />
									</div>
								</Form>
							) : (
								<Form
									form={step2PasswordForm}
									layout="vertical"
									onFinish={handleRegisterPasswordSubmit}
									className="login-form"
								>
									<div className="login-identity-chip">
										<UserOutlined /> {registerEmail}
									</div>
									<Form.Item name="password" rules={passwordRules}>
										<Input.Password
											size="large"
											prefix={<LockOutlined />}
											placeholder="Create password"
											autoComplete="new-password"
											autoFocus
										/>
									</Form.Item>
									<Form.Item
										name="confirmPassword"
										dependencies={["password"]}
										rules={[
											{
												required: true,
												message: "Confirm Password is required",
											},
											confirmRule("password"),
										]}
									>
										<Input.Password
											size="large"
											prefix={<LockOutlined />}
											placeholder="Confirm password"
											autoComplete="new-password"
										/>
									</Form.Item>
									<Form.Item>
										<div className="login-step-actions">
											<Button
												onClick={handleRegisterAccountBack}
												block
												size="large"
											>
												Back
											</Button>
											<Button
												type="primary"
												htmlType="submit"
												loading={loading}
												block
												size="large"
												className="login-btn-primary"
											>
												Create account
											</Button>
										</div>
									</Form.Item>
								</Form>
							)}
						</>
					)}

					{mode === "login" && loginStep === 0 && (
						<>
							<Divider plain className="login-divider">
								OR
							</Divider>
							<div className="login-google-wrap">
								<GoogleLoginButton onLogin={handleGoogleLogin} />
							</div>
						</>
					)}

					<div className="login-switch-text">
						{mode === "login" ? (
							<>
								Don't have an account?{" "}
								<a onClick={() => switchMode("register")}>Register</a>
							</>
						) : (
							<>
								Already have an account?{" "}
								<a onClick={() => switchMode("login")}>Login</a>
							</>
						)}
					</div>
				</Card>
			</div>
		</div>
	);
}